import React, {  useState } from 'react';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Button,
  IconButton,
  Snackbar,
  Alert
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { API_BASE_URL } from 'src/apiConfig';

const ChangePasswordDialog = ({ open, onClose, username }) => {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [errorMessages, setErrorMessages] = useState({});
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const { t } = useTranslation();

  const handleChangePassword = async () => {
    if (newPassword !== confirmPassword) {
      setErrorMessages({ password: t('profile.passwordMismatch') });
      return;
    }
    try {
      await axios.post(`${API_BASE_URL}/api/auth.php`, {
        action: 'changePassword',
        username: username,
        currentPassword,
        newPassword
      });
      setSnackbarOpen(true);
      onClose();
    } catch (error) {
      console.error('Error changing password:', error);
      setErrorMessages({ password: t('profile.passwordChangeError') });
    }
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            overflow: 'hidden',
            borderRadius: 3,
            boxShadow: 4,
            maxHeight: '80vh',
            padding: '20px',
            backgroundColor: '#fff'
          }
        }}
      >
        <DialogTitle
          sx={{
            backgroundColor: 'transparent',
            color: '#333',
            fontSize: '1.25rem', // Manji naslov
            fontWeight: 'bold',
            textAlign: 'center',
            paddingBottom: '8px',
            position: 'relative'
          }}
        >
          {t('profile.changePassword')}
          <IconButton
            edge="end"
            color="inherit"
            onClick={onClose}
            aria-label="close"
            sx={{
              position: 'absolute',
              top: 8,
              right: 8,
              color: '#555'
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent
          sx={{
            paddingX: 3,
            paddingY: 4,
            display: 'flex',
            flexDirection: 'column',
            gap: 3,
            paddingTop: '32px !important'
          }}
        >
          <TextField
            autoFocus
            id="currentPassword"
            label={t('profile.currentPassword')}
            type="password"
            fullWidth
            variant="outlined"
            value={currentPassword}
            onChange={(e) => setCurrentPassword(e.target.value)}
            error={Boolean(errorMessages.password)}
            helperText={errorMessages.password}
            sx={{
              mb: 2,
              '& .MuiOutlinedInput-root': {
                borderRadius: 3,
                backgroundColor: '#f9f9f9',
                '&:hover': {
                  backgroundColor: '#f0f0f0'
                }
              }
            }}
          />
          <TextField
            id="newPassword"
            label={t('profile.newPassword')}
            type="password"
            fullWidth
            variant="outlined"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            sx={{
              mb: 2,
              '& .MuiOutlinedInput-root': {
                borderRadius: 3,
                backgroundColor: '#f9f9f9',
                '&:hover': {
                  backgroundColor: '#f0f0f0'
                }
              }
            }}
          />
          <TextField
            id="confirmPassword"
            label={t('profile.confirmPassword')}
            type="password"
            fullWidth
            variant="outlined"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            sx={{
              mb: 2,
              '& .MuiOutlinedInput-root': {
                borderRadius: 3,
                backgroundColor: '#f9f9f9',
                '&:hover': {
                  backgroundColor: '#f0f0f0'
                }
              }
            }}
          />
        </DialogContent>
        <DialogActions
          sx={{
            padding: 2,
            justifyContent: 'space-between',
            backgroundColor: '#fff'
          }}
        >
          <Button
            onClick={onClose}
            sx={{
              color: '#333',
              backgroundColor: '#e0e0e0',
              '&:hover': {
                backgroundColor: '#d5d5d5'
              },
              borderRadius: 3,
              padding: '8px 16px',
              fontSize: '0.875rem',
              textTransform: 'none'
            }}
          >
            {t('profile.cancel')}
          </Button>
          <Button
            onClick={handleChangePassword}
            sx={{
              backgroundColor: '#2596be',
              color: '#fff',
              '&:hover': {
                backgroundColor: '#1976d2'
              },
              borderRadius: 3,
              padding: '8px 16px',
              fontWeight: 'bold',
              fontSize: '0.875rem',
              textTransform: 'none'
            }}
          >
            {t('profile.changePassword')}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity="success"
          sx={{ width: '100%' }}
        >
          {t('profile.passwordChangeSuccess')}
        </Alert>
      </Snackbar>
    </>
  );
};

export default ChangePasswordDialog;
